/**
 * Словарь `domain.objectKind` — один владелец на весь вьюер.
 *
 * До этого файла те же строки перечислялись от руки в пяти местах:
 * `isPanelLikeEntity` (`entityDomain.ts`), `FurnitureSceneEntity.isPart`
 * (`furnitureEntity.ts`), `PART_KINDS` (`anchorTarget.ts`),
 * `FurnitureEntityVisibility.isApartmentFurniture` (`furnitureVisibility.ts`)
 * — плюс пары `module|cabinet_module` и `hardware|module_hardware` в каждом
 * из них.  Списки уже разошлись: `isPart()` считает деталью `visual`, а
 * `isPanelLikeEntity` — нет (на диске это 265 сущностей из 91 192).
 *
 * Здесь НЕ решается, кто прав.  Здесь словарь получает одно имя на каждый
 * набор, а предикаты складываются из этих наборов — так расхождение видно в
 * подписи, а не спрятано в четвёртой переписанной от руки строке.
 *
 * Добавлять вид сюда — значит менять ответ сразу у всех, кто этот набор
 * назвал.  Это и есть цель: сейчас `facade_astragal` (нащельник) не знает ни
 * один из наборов, и чтобы это починить, пришлось бы править пять списков.
 */

/**
 * Щитовые детали модуля.  Ровно тот состав, что был у `isPanelLikeEntity` и
 * у `FurnitureSceneEntity.isPart()` до объединения.
 */
export const PANEL_LIKE_OBJECT_KINDS: ReadonlySet<string> = new Set([
  'panel',
  'module_panel',
  'facade',
  'drawer_front',
  'drawer_box',
  'worktop',
]);

/** Сам модуль (шкаф), а не его начинка. */
export const MODULE_OBJECT_KINDS: ReadonlySet<string> = new Set(['module', 'cabinet_module']);

/** Фурнитура: петли, направляющие, ручки. */
export const HARDWARE_OBJECT_KINDS: ReadonlySet<string> = new Set(['hardware', 'module_hardware']);

/** Декоративная вставка без паспорта детали. */
export const VISUAL_OBJECT_KIND = 'visual';

/**
 * Мебель в составе квартиры.  Шире щитовых деталей: сюда входят и сам модуль,
 * и фурнитура, и обобщённое `furniture` из чужих графов.
 */
export const APARTMENT_FURNITURE_OBJECT_KINDS: ReadonlySet<string> = new Set([
  'furniture',
  ...MODULE_OBJECT_KINDS,
  ...PANEL_LIKE_OBJECT_KINDS,
  ...HARDWARE_OBJECT_KINDS,
]);

/**
 * Что якорь считает «деталью», к которой можно привязаться: щит, фурнитура
 * или декоративная вставка.
 */
export const ANCHOR_PART_OBJECT_KINDS: ReadonlySet<string> = new Set([
  ...PANEL_LIKE_OBJECT_KINDS,
  ...HARDWARE_OBJECT_KINDS,
  VISUAL_OBJECT_KIND,
]);

/** Единственное место, где `objectKind` приводится к сравнимому виду. */
export function normalizeObjectKind(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * Виды, по которым сцена опознаётся как архитектурная (квартира/здание), а не
 * как отдельный шкаф.  Этот же список трижды переписывался от руки —
 * в `SceneCanvas`, `ViewportHud` и `useGltfLoader`.
 */
export const APARTMENT_SCENE_OBJECT_KINDS: ReadonlySet<string> = new Set([
  'apartment',
  'building',
  'apartment_shell',
  'apartment_room',
]);
